import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
import { SettingsIcon, BellIcon, ClockIcon, LinkIcon, SaveIcon, CheckCircleIcon, AlertCircleIcon, InfoIcon, LoaderIcon } from 'lucide-react';
import { useTheme } from '../providers/ThemeProvider';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
export const SettingsPage = () => {
  const {
    currentUser
  } = useAuth() as any;
  const {
    theme,
    setTheme
  } = useTheme();
  const darkMode = theme === 'dark';
  // Initial settings state
  const initialSettings = {
    emailNotifications: true,
    smsNotifications: false,
    lessonReminders: true,
    marketingEmails: false,
    defaultLessonDuration: '60',
    timeZone: 'America/New_York',
    bufferTime: '15',
    lessonFormat: 'online'
  };
  // Toggle states for notification settings
  const [emailNotifications, setEmailNotifications] = useState(initialSettings.emailNotifications);
  const [smsNotifications, setSmsNotifications] = useState(initialSettings.smsNotifications);
  const [lessonReminders, setLessonReminders] = useState(initialSettings.lessonReminders);
  const [marketingEmails, setMarketingEmails] = useState(initialSettings.marketingEmails);
  // Lesson settings
  const [defaultLessonDuration, setDefaultLessonDuration] = useState(initialSettings.defaultLessonDuration);
  const [timeZone, setTimeZone] = useState(initialSettings.timeZone);
  const [bufferTime, setBufferTime] = useState(initialSettings.bufferTime);
  const [lessonFormat, setLessonFormat] = useState(initialSettings.lessonFormat);
  // Track original settings for change detection
  const [originalSettings, setOriginalSettings] = useState({
    ...initialSettings
  });
  // UI state
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [saveMessage, setSaveMessage] = useState('');
  const [saveError, setSaveError] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState<{
    title: string;
    message: string;
    action: () => void;
  } | null>(null);
  // Load settings from Firestore on mount
  useEffect(() => {
    const loadSettings = async () => {
      if (!currentUser?.uid) {
        setIsLoadingSettings(false);
        return;
      }
      try {
        setIsLoadingSettings(true);
        const teacherRef = doc(db, 'teachers', currentUser.uid);
        const teacherSnap = await getDoc(teacherRef);
        if (teacherSnap.exists()) {
          const data = teacherSnap.data();
          // Load notification preferences
          if (data.notificationPreferences) {
            setEmailNotifications(data.notificationPreferences.emailNotifications ?? true);
            setSmsNotifications(data.notificationPreferences.smsNotifications ?? false);
            setLessonReminders(data.notificationPreferences.lessonReminders ?? true);
            setMarketingEmails(data.notificationPreferences.marketingEmails ?? false);
          }
          // Load lesson settings
          if (data.defaultLessonDuration) {
            setDefaultLessonDuration(data.defaultLessonDuration.toString());
          }
          if (data.timeZone) {
            setTimeZone(data.timeZone);
          }
          if (data.bufferTime !== undefined) {
            setBufferTime(data.bufferTime.toString());
          }
          if (data.lessonFormat) {
            setLessonFormat(data.lessonFormat);
          }
          // Update original settings to match loaded data
          setOriginalSettings({
            emailNotifications: data.notificationPreferences?.emailNotifications ?? true,
            smsNotifications: data.notificationPreferences?.smsNotifications ?? false,
            lessonReminders: data.notificationPreferences?.lessonReminders ?? true,
            marketingEmails: data.notificationPreferences?.marketingEmails ?? false,
            defaultLessonDuration: data.defaultLessonDuration?.toString() ?? '60',
            timeZone: data.timeZone ?? 'America/New_York',
            bufferTime: data.bufferTime?.toString() ?? '15',
            lessonFormat: data.lessonFormat ?? 'online'
          });
        }
      } catch (error) {
        console.error('Error loading settings:', error);
        setSaveError('Failed to load settings. Please refresh the page.');
      } finally {
        setIsLoadingSettings(false);
      }
    };
    loadSettings();
  }, [currentUser]);
  // Check if any settings have changed
  const hasChanges = () => {
    return emailNotifications !== originalSettings.emailNotifications || smsNotifications !== originalSettings.smsNotifications || lessonReminders !== originalSettings.lessonReminders || marketingEmails !== originalSettings.marketingEmails || defaultLessonDuration !== originalSettings.defaultLessonDuration || timeZone !== originalSettings.timeZone || bufferTime !== originalSettings.bufferTime || lessonFormat !== originalSettings.lessonFormat;
  };
  // Handle save with Firestore persistence
  const handleSave = async () => {
    if (!hasChanges()) return;
    if (!currentUser?.uid) {
      setSaveError('You must be logged in to save settings');
      return;
    }
    setIsSaving(true);
    setSaveError('');
    setSaveMessage('');
    try {
      const teacherRef = doc(db, 'teachers', currentUser.uid);
      // Prepare update data
      const updateData = {
        notificationPreferences: {
          emailNotifications,
          smsNotifications,
          lessonReminders,
          marketingEmails
        },
        defaultLessonDuration: parseInt(defaultLessonDuration),
        timeZone,
        bufferTime: parseInt(bufferTime),
        lessonFormat,
        updatedAt: serverTimestamp()
      };
      // If timezone was changed manually, mark it as user-selected
      if (timeZone !== originalSettings.timeZone) {
        updateData.timezoneAutoDetected = false; // User manually changed timezone
        console.log('[Settings] User manually changed timezone to:', timeZone);
      }
      // Write to Firestore
      await updateDoc(teacherRef, updateData);
      // Also update localStorage for offline access
      const teacherProfile = JSON.parse(localStorage.getItem('mc_teacher_profile') || '{}');
      teacherProfile.defaultLessonDuration = parseInt(defaultLessonDuration);
      teacherProfile.timeZone = timeZone;
      teacherProfile.bufferTime = parseInt(bufferTime);
      teacherProfile.lessonFormat = lessonFormat;
      teacherProfile.notificationPreferences = {
        emailNotifications,
        smsNotifications,
        lessonReminders,
        marketingEmails
      };
      localStorage.setItem('mc_teacher_profile', JSON.stringify(teacherProfile));
      // Update original settings to match current state
      setOriginalSettings({
        emailNotifications,
        smsNotifications,
        lessonReminders,
        marketingEmails,
        defaultLessonDuration,
        timeZone,
        bufferTime,
        lessonFormat
      });
      setSaveMessage('Settings saved successfully');
      setTimeout(() => {
        setSaveMessage('');
      }, 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setSaveError('Failed to save settings. Please check your connection and try again.');
      setTimeout(() => {
        setSaveError('');
      }, 5000);
    } finally {
      setIsSaving(false);
    }
  };
  // Show confirmation dialog
  const showConfirmDialog = (title: string, message: string, action: () => void) => {
    setConfirmationAction({
      title,
      message,
      action
    });
    setShowConfirmation(true);
  };
  // Loading state
  if (isLoadingSettings) {
    return <main className="pb-6" data-id="element-6725">
        <div className="max-w-7xl mx-auto px-4" data-id="element-6726">
          <div className="flex flex-col items-center justify-center py-12" data-id="element-6727">
            <LoaderIcon className="h-8 w-8 text-green-500 animate-spin mb-4" data-id="element-6728" />
            <p className={`${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-6729">
              Loading your settings...
            </p>
          </div>
        </div>
      </main>;
  }
  return <main className="pb-6" data-id="element-6730">
      <div className="max-w-7xl mx-auto px-4" data-id="element-6731">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6" data-id="element-6732">
          <div className="mb-4 md:mb-0" data-id="element-6733">
            <h1 className="text-2xl sm:text-3xl font-bold" data-id="element-6734">Settings</h1>
          </div>
          {/* Save button - only show if there are changes */}
          {hasChanges() && <button onClick={handleSave} disabled={isSaving} className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isSaving ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-500 hover:bg-green-600'} text-white`} data-id="element-6735">
              {isSaving ? <>
                  <LoaderIcon className="h-4 w-4 mr-2 animate-spin" data-id="element-6736" />
                  Saving...
                </> : <>
                  <SaveIcon className="h-4 w-4 mr-2" data-id="element-6737" />
                  Save Changes
                </>}
            </button>}
        </div>

        {/* Success Message */}
        {saveMessage && <div className={`mb-6 p-4 rounded-lg ${darkMode ? 'bg-green-500/20 text-green-300' : 'bg-green-100 text-green-700'} flex items-center`} role="alert" data-id="element-6738">
            <CheckCircleIcon className="h-5 w-5 mr-2" data-id="element-6739" />
            {saveMessage}
          </div>}

        {/* Error Message */}
        {saveError && <div className={`mb-6 p-4 rounded-lg ${darkMode ? 'bg-red-500/20 text-red-300' : 'bg-red-100 text-red-700'} flex items-center`} role="alert" data-id="element-6740">
            <AlertCircleIcon className="h-5 w-5 mr-2" data-id="element-6741" />
            {saveError}
          </div>}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-6742">
          {/* Notification Settings */}
          <div className={`${darkMode ? 'bg-navy-800/80' : 'bg-white'} rounded-xl shadow-sm p-6 lg:col-span-2`} data-id="element-6743">
            <div className="flex items-center mb-4" data-id="element-6744">
              <BellIcon className="h-5 w-5 text-green-500 mr-2" aria-hidden="true" data-id="element-6745" />
              <h2 className="text-xl font-serif font-semibold" data-id="element-6746">
                Notification Preferences
              </h2>
            </div>
            <div className="space-y-4" data-id="element-6747">
              <div className={`flex items-center justify-between p-4 rounded-lg ${darkMode ? 'bg-navy-900/50' : 'bg-gray-50'}`} data-id="element-6748">
                <div className="flex-1" data-id="element-6749">
                  <h3 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-6750">
                    Email Notifications
                  </h3>
                  <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-6751">
                    Receive important updates via email
                  </p>
                </div>
                <div className="pl-4 pr-2" data-id="element-6752">
                  <button onClick={() => setEmailNotifications(!emailNotifications)} className="focus:outline-none" aria-pressed={emailNotifications} data-id="element-6753">
                    {emailNotifications ? <div className="h-6 w-11 bg-green-500 rounded-full relative" data-id="element-6754">
                        <div className="h-5 w-5 bg-white rounded-full absolute right-0.5 top-0.5 transition-transform" data-id="element-6755"></div>
                      </div> : <div className={`h-6 w-11 rounded-full relative ${darkMode ? 'bg-gray-600' : 'bg-gray-300'}`} data-id="element-6756">
                        <div className="h-5 w-5 bg-white rounded-full absolute left-0.5 top-0.5 transition-transform" data-id="element-6757"></div>
                      </div>}
                  </button>
                </div>
              </div>

              <div className={`flex items-center justify-between p-4 rounded-lg ${darkMode ? 'bg-navy-900/50' : 'bg-gray-50'}`} data-id="element-6758">
                <div className="flex-1" data-id="element-6759">
                  <h3 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-6760">
                    SMS Notifications
                  </h3>
                  <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-6761">
                    Receive text messages for urgent updates
                  </p>
                </div>
                <div className="pl-4 pr-2" data-id="element-6762">
                  <button onClick={() => setSmsNotifications(!smsNotifications)} className="focus:outline-none" aria-pressed={smsNotifications} data-id="element-6763">
                    {smsNotifications ? <div className="h-6 w-11 bg-green-500 rounded-full relative" data-id="element-6764">
                        <div className="h-5 w-5 bg-white rounded-full absolute right-0.5 top-0.5 transition-transform" data-id="element-6765"></div>
                      </div> : <div className={`h-6 w-11 rounded-full relative ${darkMode ? 'bg-gray-600' : 'bg-gray-300'}`} data-id="element-6766">
                        <div className="h-5 w-5 bg-white rounded-full absolute left-0.5 top-0.5 transition-transform" data-id="element-6767"></div>
                      </div>}
                  </button>
                </div>
              </div>

              <div className={`flex items-center justify-between p-4 rounded-lg ${darkMode ? 'bg-navy-900/50' : 'bg-gray-50'}`} data-id="element-6768">
                <div className="flex-1" data-id="element-6769">
                  <h3 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-6770">
                    Lesson Reminders
                  </h3>
                  <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-6771">
                    Get reminders before scheduled lessons
                  </p>
                </div>
                <div className="pl-4 pr-2" data-id="element-6772">
                  <button onClick={() => setLessonReminders(!lessonReminders)} className="focus:outline-none" aria-pressed={lessonReminders} data-id="element-6773">
                    {lessonReminders ? <div className="h-6 w-11 bg-green-500 rounded-full relative" data-id="element-6774">
                        <div className="h-5 w-5 bg-white rounded-full absolute right-0.5 top-0.5 transition-transform" data-id="element-6775"></div>
                      </div> : <div className={`h-6 w-11 rounded-full relative ${darkMode ? 'bg-gray-600' : 'bg-gray-300'}`} data-id="element-6776">
                        <div className="h-5 w-5 bg-white rounded-full absolute left-0.5 top-0.5 transition-transform" data-id="element-6777"></div>
                      </div>}
                  </button>
                </div>
              </div>

              <div className={`flex items-center justify-between p-4 rounded-lg ${darkMode ? 'bg-navy-900/50' : 'bg-gray-50'}`} data-id="element-6778">
                <div className="flex-1" data-id="element-6779">
                  <h3 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-6780">
                    Marketing Emails
                  </h3>
                  <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-6781">
                    Receive promotional emails and offers
                  </p>
                </div>
                <div className="pl-4 pr-2" data-id="element-6782">
                  <button onClick={() => setMarketingEmails(!marketingEmails)} className="focus:outline-none" aria-pressed={marketingEmails} data-id="element-6783">
                    {marketingEmails ? <div className="h-6 w-11 bg-green-500 rounded-full relative" data-id="element-6784">
                        <div className="h-5 w-5 bg-white rounded-full absolute right-0.5 top-0.5 transition-transform" data-id="element-6785"></div>
                      </div> : <div className={`h-6 w-11 rounded-full relative ${darkMode ? 'bg-gray-600' : 'bg-gray-300'}`} data-id="element-6786">
                        <div className="h-5 w-5 bg-white rounded-full absolute left-0.5 top-0.5 transition-transform" data-id="element-6787"></div>
                      </div>}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Integrations - Coming Soon */}
          <div className={`${darkMode ? 'bg-navy-800/80' : 'bg-white'} rounded-xl shadow-sm p-6 lg:col-span-1`} data-id="element-6788">
            <div className="flex items-center mb-4" data-id="element-6789">
              <LinkIcon className="h-5 w-5 text-green-500 mr-2" aria-hidden="true" data-id="element-6790" />
              <h2 className="text-xl font-serif font-semibold" data-id="element-6791">Integrations</h2>
            </div>
            <div className={`p-4 rounded-lg ${darkMode ? 'bg-navy-900/50 border border-navy-700' : 'bg-gray-50 border border-gray-200'} text-center`} data-id="element-6792">
              <InfoIcon className={`h-8 w-8 mx-auto mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-6793" />
              <p className={`text-sm font-medium mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-6794">
                Coming Soon
              </p>
              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-6795">
                Calendar and video integrations will be available in a future
                update
              </p>
            </div>
          </div>

          {/* Default Lesson Settings */}
          <div className={`${darkMode ? 'bg-navy-800/80' : 'bg-white'} rounded-xl shadow-sm p-6 lg:col-span-2`} data-id="element-6796">
            <div className="flex items-center mb-4" data-id="element-6797">
              <ClockIcon className="h-5 w-5 text-green-500 mr-2" aria-hidden="true" data-id="element-6798" />
              <h2 className="text-xl font-serif font-semibold" data-id="element-6799">
                Default Lesson Settings
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-6800">
              <div data-id="element-6801">
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-6802">
                  Time Zone
                </label>
                <select className={`w-full px-4 py-2 rounded-lg ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white focus:ring-green-500 focus:border-green-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-green-500 focus:border-green-500'} border`} value={timeZone} onChange={e => setTimeZone(e.target.value)} data-id="element-6803">
                  <option value="America/New_York" data-id="element-6804">
                    Eastern Time (US & Canada)
                  </option>
                  <option value="America/Chicago" data-id="element-6805">
                    Central Time (US & Canada)
                  </option>
                  <option value="America/Denver" data-id="element-6806">
                    Mountain Time (US & Canada)
                  </option>
                  <option value="America/Los_Angeles" data-id="element-6807">
                    Pacific Time (US & Canada)
                  </option>
                  <option value="Europe/London" data-id="element-6808">London</option>
                  <option value="Europe/Paris" data-id="element-6809">Paris</option>
                  <option value="Asia/Tokyo" data-id="element-6810">Tokyo</option>
                </select>
              </div>

              <div data-id="element-6811">
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-6812">
                  Default Lesson Format
                </label>
                <select className={`w-full px-4 py-2 rounded-lg ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white focus:ring-green-500 focus:border-green-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-green-500 focus:border-green-500'} border`} value={lessonFormat} onChange={e => setLessonFormat(e.target.value)} data-id="element-6813">
                  <option value="online" data-id="element-6814">Online</option>
                  <option value="in-person" data-id="element-6815">In-person</option>
                  <option value="hybrid" data-id="element-6816">Hybrid</option>
                </select>
              </div>
            </div>
          </div>

          {/* Empty slot for layout */}
          <div className="lg:col-span-1" data-id="element-6817"></div>
        </div>
      </div>
    </main>;
};